"use client";
import React from 'react';
import { motion } from 'framer-motion';

const items = [
    {
        title: 'Brand & Entity Clarity',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
        desc: 'We help establish a clearer understanding of who your business is, what you offer, who you serve and how your expertise relates to your category.',
    },
    {
        title: 'Answer-Focused Content',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="2" />
                <path d="M16 21v-6M16 11.5v-.1" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We structure important information around the questions customers actually ask, providing direct explanations rather than burying useful answers under generic marketing copy.',
    },
    {
        title: 'Topical Authority',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="5" r="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="27" cy="16" r="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="27" r="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="5" cy="16" r="2.5" stroke="currentColor" strokeWidth="2" />
                <path d="M16 12v-4M20 16h4M16 20v4M12 16H8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We build connected content around important subjects instead of isolated pages targeting individual keywords.',
    },
    {
        title: 'First-Hand Expertise',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <path d="M16 4l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
        desc: "Your strongest content shouldn't sound like it could have been written by anyone. We bring real experience, processes, examples, insights and client work into the content wherever possible.",
    },
    {
        title: 'Technical Accessibility',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <circle cx="14" cy="14" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'AI-powered search still needs to access and understand web content. We make sure important information is crawlable, indexable and clearly connected across the site.',
    },
    {
        title: 'Structured Information',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <rect x="6" y="4" width="20" height="24" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <path d="M11 11h10M11 16h10M11 21h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'Where appropriate, we use structured data and clear page structures to help search engines interpret your organisation, services and other relevant entities.',
    },
];

export default function AISEOWhatWeOptimise() {
    return (
        <section className="relative overflow-hidden py-16 md:py-24 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff' }}>
            <div className="max-w-[1400px] mx-auto flex flex-col gap-14">
                <div className="flex flex-col gap-4">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: '#3730a3', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        Our Focus Areas
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#080400' }}
                    >
                        What We Optimise
                    </motion.h2>
                </div>

                <div
                    className="grid grid-cols-1 md:grid-cols-3 rounded-3xl overflow-hidden"
                    style={{ border: '1px solid rgba(8,4,0,0.15)' }}
                >
                    {items.map((it, i) => {
                        const isLastCol = i % 3 === 2;
                        const isLastItem = i === items.length - 1;
                        const isFirstRow = i < 3;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                                className={[
                                    'p-8 flex flex-col gap-4 transition-colors duration-300 hover:bg-[rgba(8,4,0,0.02)] border-[rgba(8,4,0,0.12)]',
                                    !isLastItem ? 'border-b' : '',
                                    !isFirstRow ? 'md:border-b-0' : '',
                                    !isLastCol ? 'md:border-r' : '',
                                ].join(' ')}
                            >
                                <div
                                    className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                                    style={{ background: 'rgba(55,48,163,0.08)', border: '1px solid rgba(55,48,163,0.18)', color: '#3730a3' }}
                                >
                                    {it.icon}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <h3
                                        className="text-lg font-bold leading-snug"
                                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#080400', letterSpacing: '0.02em' }}
                                    >
                                        {it.title}
                                    </h3>
                                    <p
                                        className="text-sm leading-relaxed"
                                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.6)', letterSpacing: '0.01em' }}
                                    >
                                        {it.desc}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="rounded-2xl px-6 py-5"
                    style={{ background: 'rgba(55,48,163,0.05)', border: '1px solid rgba(55,48,163,0.15)' }}
                >
                    <p
                        className="text-sm md:text-base leading-relaxed"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.6)', letterSpacing: '0.01em' }}
                    >
                        Google&apos;s latest guidance specifically recommends valuable non-commodity content, clear organisation, technical accessibility and first-hand expertise, while noting that there is no special AI schema or technical trick required.
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
